import type { ReactNode } from "react";
import { SlideHeader } from "../../../SlideHeader";
import { SupLink } from "../../../SlideContext";

export default function ShapeAndUseOfFunds() {
  return (
    <div className="flex w-full flex-col px-6 pt-8 pb-4 text-slate-900 md:h-full md:px-24 md:pt-16 md:pb-8">
      <SlideHeader eyebrow="Capitalization">
        <h1 className="text-3xl font-semibold leading-tight tracking-tight md:text-6xl md:leading-[1.1]">
          €900K pre-seed on a €6M post-money SAFE.
        </h1>
      </SlideHeader>
      <div className="hidden md:block md:flex-1" />

      <div className="grid grid-cols-1 gap-6 border-t border-slate-200 pt-6 md:grid-cols-3 md:gap-12 md:pt-10">
        <Signal label="Team">
          Operator-founders. 30+ years of product and ASO across{" "}
          <span className="font-semibold text-slate-900">
            Netflix, Google, FunPlus, KiloHealth, and Welltech
          </span>
          . EU-based.
        </Signal>
        <Signal label="Traction">
          <span className="font-semibold text-slate-900">Production-live</span>{" "}
          with paying customers and active design partners. Pilot conversations
          underway with major app and game publishers.{" "}
          <span className="font-semibold text-slate-900">
            Impact attribution runs on the customer's own store data
          </span>{" "}
          — conversion and visibility deltas, tied to specific shipped
          recommendations.
        </Signal>
        <Signal label="Why now">
          App discovery is{" "}
          <span className="font-semibold text-slate-900">fragmenting fast</span>{" "}
          — across iOS, Google Play, and the AI surfaces customers now ask
          first. Incumbents rebranded with AI; few have transformed. And both
          ASO inputs —{" "}
          <span className="font-semibold text-slate-900">
            visibility and conversion — decay continuously
          </span>
          . The organic-install gap grows every week.
        </Signal>
      </div>

      {/* Use of funds + m12 target (A7Y-1926; 1822 Tier-2) */}
      <div className="mt-6 md:mt-8">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-700 md:tracking-[0.2em]">
          Use of funds
        </p>
        <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-6">
          <Fund pct="44%" label="Product & core team" amount="€396K" />
          <Fund pct="44%" label="Marketing" amount="€396K" />
          <Fund pct="8%" label="Ops & admin" amount="€72K" />
          <Fund pct="4%" label="Reserve" amount="€36K" />
        </div>
        <p className="mt-3 text-sm text-slate-600 md:text-base">
          <span className="font-semibold text-slate-900">What it funds:</span>{" "}
          the month-12 engine — ~€1.55M ARR run-rate on 700 paying accounts.
        </p>
      </div>

      {/* Act III closer — what success looks like (€125M anchor relocated
          here from The ASO Market H1 per A7Y-1926) */}
      <div className="mt-6 rounded-2xl bg-blue-50 px-6 py-5 text-center ring-1 ring-blue-200 md:mt-10 md:px-10 md:py-6">
        <p className="text-balance text-lg font-semibold leading-snug text-blue-900 md:text-2xl">
          The target:{" "}
          <span className="font-bold">
            €125M enterprise value by month 24
          </span>
          <SupLink to="ev-target" className="text-blue-700">G</SupLink> — on
          the way to every app team running expert marketing, continuously.
        </p>
      </div>
    </div>
  );
}

function Fund({
  pct,
  label,
  amount,
}: {
  pct: string;
  label: string;
  amount: string;
}) {
  return (
    <div className="rounded-lg bg-slate-50 p-3 ring-1 ring-slate-200 md:p-4">
      <p className="text-xl font-bold tracking-tight text-blue-700 md:text-2xl">
        {pct}
      </p>
      <p className="mt-0.5 text-xs font-medium text-slate-900 md:text-sm">
        {label}
      </p>
      <p className="text-xs text-slate-500 md:text-sm">{amount}</p>
    </div>
  );
}

function Signal({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-700 md:tracking-[0.2em]">
        {label}
      </p>
      <p className="mt-2 text-sm leading-relaxed text-slate-700 md:mt-3 md:text-lg">
        {children}
      </p>
    </div>
  );
}
